import React, { useEffect, useState } from 'react';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { analyticsLogger } from 'commons/src/analytics';
import { BUILDING_HEATING_COOLING_INSIGHT_FETCHED } from 'commons/src/analytics/AnalyticsEvents';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import { IntercomAPI } from 'commons/src/components/Intercom';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { Resolution, TimePeriod } from 'commons/src/models/commonEnums';
import { ErrorType } from 'commons/src/models/commonTypeScript';
import { getHeatingCoolingData } from '../../../actions/buildingOptimizationActions';
import { Store } from '../../../reducers';
import { BusinessRequestType } from '../../../reducers/BusinessRequestType';
import styles from './EnergyInsightPage.module.scss';
import EnergyInsightTimeSelector from './EnergyInsightTimeSelector';
import HeatingCoolingCSVDownload from './HeatingCoolingCSVDownload';
import HeatingCoolingGraph from './HeatingCoolingGraph';

type EnergyInsightSelectedPeriod = {
    to: string;
    from: string;
    name: TimePeriod;
    resolution: Resolution;
    locationId?: string;
};

type ParentProps = {
    locationName: string;
    openingHoursEnabled: boolean;
};

const defaultTimeSpanSelected: TimePeriod = TimePeriod.week;
const initialSelectedPeriod: EnergyInsightSelectedPeriod = {
    to: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    from: moment().subtract(1, defaultTimeSpanSelected).format('YYYY-MM-DD'),
    name: defaultTimeSpanSelected,
    resolution: Resolution.day,
};

const EnergyInsightPage = ({ locationName, openingHoursEnabled }: ParentProps): React.ReactElement => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const { buildingId } = useParams<'buildingId'>() as { buildingId: string };

    const dateFormat = useSelector((state: Store) => state.userSettings.dateFormat);
    const language = useSelector((state: Store) => state.userSettings.language);
    const buildings = useSelector((state: Store) => state.buildings.buildings);
    const heatingCoolingDataFromStore = useSelector((state: Store) => state.buildingOptimization.heatingCoolingData);
    const selectedPeriodInStore = heatingCoolingDataFromStore?.selectedPeriod;
    const heatingCoolingData = heatingCoolingDataFromStore?.data;
    const loading = useSelector(
        (state: Store) => state.requests[BusinessRequestType.GetHeatingCoolingData]?.loading || false
    );
    const error = useSelector((state: Store) => state.requests[BusinessRequestType.GetHeatingCoolingData]?.error) as
        | ErrorType
        | undefined;
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const useSelectedPeriodFromStore =
        !!heatingCoolingData &&
        selectedPeriodInStore?.locationId === buildingId &&
        selectedPeriodInStore?.name !== TimePeriod.custom;
    const [selectedPeriod, setSelectedPeriod] = useState<EnergyInsightSelectedPeriod>(
        selectedPeriodInStore && useSelectedPeriodFromStore ? selectedPeriodInStore : initialSelectedPeriod
    );
    const building = buildings[buildingId];
    const physicalDevices = building?.devices || [];

    useEffect(() => {
        if (!useSelectedPeriodFromStore) {
            const payload = {
                ...initialSelectedPeriod,
                locationId: buildingId,
            };
            analyticsLogger(BUILDING_HEATING_COOLING_INSIGHT_FETCHED, { payload });
            dispatch(getHeatingCoolingData(payload));
        }
    }, []);

    const selectPeriod = (period: TimePeriod, from: string, to: string, resolution: Resolution): void => {
        setSelectedPeriod({
            from,
            to,
            name: period,
            resolution,
        });
        const payload = {
            from,
            to,
            name: period,
            locationId: buildingId,
            resolution,
        };
        analyticsLogger(BUILDING_HEATING_COOLING_INSIGHT_FETCHED, { payload });
        dispatch(getHeatingCoolingData(payload));
    };

    const triggerIntercomSurvey = (): void => {
        IntercomAPI('startSurvey', 171639);
    };

    useEffect(() => {
        if (building?.timezone) {
            Highcharts.setOptions({
                time: {
                    useUTC: false,
                    timezone: building?.timezone,
                    timezoneOffset: -moment.tz(building?.timezone).utcOffset(),
                },
            });
        }
    }, [building?.timezone]);

    return (
        <div className="container">
            <SectionHeader headerName="BuildingEnergy.EnergyOptimization" headerType={3} initialHeader />
            <div className={styles.energyDescription}>{txt('BuildingEnergy.EnergyDescription')}</div>
            <SectionHeader
                headerName="BuildingEnergy.HeatingAndCooling"
                infoText="BuildingEnergy.HeaderInfoText"
                headerType={4}
                endOfLineButton={
                    <TertiaryButton onClick={triggerIntercomSurvey} title="BuildingEnergy.HeatingCoolingFeedback" />
                }
            />
            <div className={styles.timeSelectorWrapper}>
                <EnergyInsightTimeSelector selectedPeriod={selectedPeriod.name} setSelectedPeriod={selectPeriod} />
                {heatingCoolingData && downloadModalOpen && (
                    <HeatingCoolingCSVDownload
                        dateFormat={dateFormat}
                        heatingCoolingData={heatingCoolingData}
                        selectedPeriod={{
                            toDate: selectedPeriod.to,
                            fromDate: selectedPeriod.from,
                            resolution: selectedPeriod.resolution,
                            name: selectedPeriod.name,
                        }}
                        setDownloadModalOpen={setDownloadModalOpen}
                        locationName={locationName}
                    />
                )}
                {heatingCoolingData && openingHoursEnabled && (
                    <PrimaryButton
                        color="secondary"
                        disabled={loading}
                        onClick={(): void => setDownloadModalOpen(true)}
                        title="CsvStrings.Csv"
                        icon={<MaterialIcon name="download" />}
                    />
                )}
            </div>
            <HeatingCoolingGraph
                locationId={buildingId}
                data={heatingCoolingData || []}
                dateFormat={dateFormat}
                loading={loading}
                language={language}
                error={error}
                openingHoursEnabled={openingHoursEnabled}
                numberOfDevicesInLocation={physicalDevices.length}
            />
        </div>
    );
};

export default EnergyInsightPage;
